import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import { updateUserSettings, logCurrentSettings } from "../settings";
import { getDashboardTasks } from "../utils/taskLogic";
import { getTaskBadge } from "../utils/taskBadge";
import { CO_LABEL } from "../utils/contractLabel";
import TrialStatusBadge from "../components/TrialStatusBadge";
import { getUserSettings } from "../settings";
import { useDebounce } from "../hooks/useDebounce";
import { TEXT_FILTER_KEYS } from "../utils/trialSearchFilters";
import {
  dashboardFiltersFrom, differsFromDefaults, hasTaskFilters, taskPasses, trialFiltersOf,
} from "../utils/dashboardFilters";
import TrialSearchFilters from "../components/TrialSearchFilters";
import DashboardTaskFilters from "../components/DashboardTaskFilters";
import { useVanoCodes } from "../context/VanoCodesContext";
import { activityLabel } from "../utils/vanoCodes";
import { formatDate } from "../utils/dateFormat";
import { parseGps } from "../utils/gps";
import { SAMPLE_FIELDS, shownSamples } from "../utils/samples";
import { TaskCalendar } from "../components/Planning/components/TaskCalendar";
import { TaskList } from "../components/Planning/components/TaskList";
import { TaskStatusLegend } from "../components/TaskStatus";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "../components/ui/resizable";

import TrialsTable from "../components/TrialsTable";

// Get current user ID from sessionStorage. Populated by user.js's getUserInfo() (awaited on
// login, and refreshed on every Layout mount) - the only source; the legacy 'user' key is
// never actually written (AuthController::login's response carries no user object), so it's
// not read here any more.
function getCurrentUserId() {
  try {
    const userRaw = sessionStorage.getItem("userInfo");
    if (!userRaw) return null;
    let user;
    try {
      user = JSON.parse(userRaw);
    } catch {
      user = userRaw;
    }
    const userData = user?.data ?? user;
    return userData?.id ?? userData?.userId ?? userData?.userid ?? null;
  } catch (e) {
    return null;
  }
}

// ISO week number of a date (week 1 has the year's first Thursday)
function isoWeek(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
}

export default function Dashboard() {
  // Safe unconditionally: Dashboard only ever renders inside PrivateRoute -> VanoCodesProvider,
  // which gates mounting children until the catalog is fetched and validated (same guarantee
  // TrialView.jsx relies on). Used by getDashboardTasks to filter hidden Activities.
  const vanoCodes = useVanoCodes();
  // The filter panel - trial filters, task filters and the toggles - one state for the task cards,
  // the calendar and the trials table, saved per user (utils/dashboardFilters.js)
  const [dashFilters, setDashFilters] = useState(() => dashboardFiltersFrom(getUserSettings().dashboard_filters));
  const filtersTouched = useRef(false);
  const updateDashFilters = (patch) => {
    filtersTouched.current = true;
    setDashFilters((f) => ({ ...f, ...patch }));
  };
  const resetDashFilters = () => {
    filtersTouched.current = true;
    setDashFilters(dashboardFiltersFrom(null));
  };
  // The user's saved filters can arrive after the page has mounted (getUserInfo on login)
  useEffect(() => {
    const apply = () => {
      if (!filtersTouched.current) setDashFilters(dashboardFiltersFrom(getUserSettings().dashboard_filters));
    };
    window.addEventListener("userSettingsHydrated", apply);
    return () => window.removeEventListener("userSettingsHydrated", apply);
  }, []);
  // Saved once the user changes them (debounced)
  useEffect(() => {
    if (!filtersTouched.current) return undefined;
    const timer = setTimeout(() => updateUserSettings({ dashboard_filters: dashFilters }), 500);
    return () => clearTimeout(timer);
  }, [dashFilters]);
  // The trial filters for the API, with the typed ones debounced
  const debouncedText = useDebounce(JSON.stringify(TEXT_FILTER_KEYS.map((k) => dashFilters[k])), 400);
  const trialQueryKey = useMemo(() => {
    const text = JSON.parse(debouncedText);
    return JSON.stringify(trialFiltersOf({ ...dashFilters, ...Object.fromEntries(TEXT_FILTER_KEYS.map((k, i) => [k, text[i]])) }));
  }, [debouncedText, dashFilters]);
  // Showing another user's tasks ("Assigned to" someone else) switches "Mine only" off for as long
  // as that lasts; the saved choice comes back when the field is cleared or set to oneself
  const assigneeIsOther = dashFilters.assignee !== "" && dashFilters.assignee !== "unassigned"
    && String(dashFilters.assignee) !== String(getCurrentUserId());
  const effectiveMineOnly = dashFilters.mineOnly && !assigneeIsOther;
  // The calendar's date filter: null (all tasks), or { kind: 'day' | 'week', from, to }. Not saved.
  const [dateSelection, setDateSelection] = useState(null);
  const latestFetch = useRef(0);
  const [trials, setTrials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const SHOW_CLOSED_KEY = "dashboardShowClosed";
  const [showClosed, setShowClosed] = useState(() => {
    try {
      const raw = sessionStorage.getItem(SHOW_CLOSED_KEY);
      if (raw == null) return false;
      return raw === "true";
    } catch {
      return false;
    }
  });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(() => {
    const stored = sessionStorage.getItem("dashboardTrialsPerPage");
    if (stored === "all") return "all";
    const num = stored ? Number(stored) : 15;
    return [15, 25, 50].includes(num) ? num : 15;
  });
  // Column configuration state
  const COLUMN_STORAGE_KEY = "dashboardTrialColumns";
  // Complete list of selectable trial fields (provided)
  // NOTE: 'id' intentionally excluded from configurable fields per request
  const ALL_TRIAL_FIELDS = [
    "customer_id",
    "projectno",
    "project_id",
    "trialno",
    "studydirector",
    "pitxt",
    "subcontractor",
    "testitem",
    "country_id",
    "crop_id",
    "locationtarget",
    "study_type_id",
    "trial_type_id",
    "host_id",
    "TrialAddress",
    "LocalReference",
    "EarthSample",
    "TreatmentCount",
    "ReplicationCount",
    "ApplicationCount",
    "VisitCount",
    "Harvest",
    "Destruction",
    "FieldFaseStart",
    "FieldFaseEnd",
    "TrialSetupARM",
    "CustomerContact",
    "PlotSizeL",
    "PlotSizeB",
    "Year",
    "subscribed_GEP_GL",
    "unsubscribed_GEP_GL",
    "Borders_Sprayed",
    "Weighed_out_Applications",
    "TS",
    "SubcEPA",
    "StOut",
    "ResAr",
    "Closed",
    "FinalReport",
    "PegsUp",
    "PlotSize",
    "WOA",
    "PO",
    "Noter1",
    "Noter2",
    "Noter3",
    "PosGPSLatitude",
    "PosGPSLongitude",
    "SubScribed_GEP",
    "UnSubScribed_GEP",
    "DraftReport",
    "GerminationTest",
    "NIT",
    "ReadyForBilling",
    "AdditionalWork",
    "TGW",
    "RecordCleansing",
    "HLLWonly",
    "EndFieldPhase",
    "ARMReady",
    "ReportProofRead",
    "TrialDone",
    "NIT_Samples",
    "GT_Samples",
    "TGW_Samples",
    "Contract_Amount",
    "Price_Reduction",
    "Second_Year_Amount",
    "Hours",
    "FT",
    "PI",
  ];
  // Default visible columns (projectno hidden by default now); trialno always required
  const defaultColumns = [
    "trialno",
    "customer_id",
    "locationtarget",
    "Year",
    "FT",
    "Closed",
  ];
  const [selectedColumns, setSelectedColumns] = useState(() => {
    try {
      const raw = sessionStorage.getItem(COLUMN_STORAGE_KEY);
      if (!raw) return defaultColumns;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        // Remove any legacy 'id'
        const filtered = parsed.filter((k) => k !== "id");
        return Array.from(new Set([...filtered, "trialno"]));
      }
      return defaultColumns;
    } catch {
      return defaultColumns;
    }
  });
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [savingColumns, setSavingColumns] = useState(false);
  const hydratedFromSettingsRef = useRef(false);
  const overviewHydratedRef = useRef(false);
  const columnMenuRef = useRef(null);

  // TrialsOverview-like state (to reuse its table rendering)
  const [massUpdateMode, setMassUpdateMode] = useState(false);
  const [selectedTrialIds, setSelectedTrialIds] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [customers, setCustomers] = useState([]);
  const [meta, setMeta] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [editValues, setEditValues] = useState({});
  const [showCopyToDialog, setShowCopyToDialog] = useState(false);
  const [newTrialNumber, setNewTrialNumber] = useState("");
  // Overview columns: prefer the dashboard-specific persisted key (left funnel saves here),
  // but fallback to the legacy trialsOverviewColumns if present.
  const OVERVIEW_COLUMN_KEY = "trialsOverviewColumns";
  const overviewDefault = [
    "trialno",
    "customer",
    "studydirector",
    "PI",
    "FT",
    "status",
  ];
  const hydratedRef = useRef(false);
  const [overviewColumns, setOverviewColumns] = useState(() => {
    try {
      // The funnel intentionally persists to the dashboard column storage key (COLUMN_STORAGE_KEY)
      // so read that first. Fallback to legacy overview key.
      const raw =
        sessionStorage.getItem(COLUMN_STORAGE_KEY) ||
        sessionStorage.getItem(OVERVIEW_COLUMN_KEY);
      if (!raw) return overviewDefault;
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) return parsed;
      return overviewDefault;
    } catch {
      return overviewDefault;
    }
  });
  const [showOverviewColumnConfig, setShowOverviewColumnConfig] =
    useState(false);
  const [savingOverviewCols, setSavingOverviewCols] = useState(false);
  const overviewMenuRef = useRef(null);

  useEffect(() => {
    sessionStorage.setItem("dashboardTrialsPerPage", String(perPage));
    // Persist to user settings (debounced via simple timeout to avoid rapid calls)
    const t = setTimeout(() => {
      updateUserSettings({ dashboard_trials_per_page: perPage });
    }, 300);
    return () => clearTimeout(t);
  }, [perPage]);

  // Persist showClosed toggle
  useEffect(() => {
    try {
      sessionStorage.setItem(SHOW_CLOSED_KEY, String(showClosed));
    } catch { }
    const t = setTimeout(() => {
      updateUserSettings({ dashboard_show_closed: showClosed });
    }, 400);
    return () => clearTimeout(t);
  }, [showClosed]);

  // Hydrate perPage from settings event if fired after mount
  useEffect(() => {
    const applyPerPage = (e) => {
      try {
        const stored = sessionStorage.getItem("dashboardTrialsPerPage");
        if (!stored) return;
        if (stored === "all") {
          if (perPage !== "all") setPerPage("all");
        } else {
          const num = Number(stored);
          if ([15, 25, 50].includes(num) && perPage !== num) setPerPage(num);
        }
      } catch { }
    };
    window.addEventListener("dashboardPerPageHydrated", applyPerPage);
    window.addEventListener("userSettingsUpdated", applyPerPage);
    applyPerPage();
    return () => {
      window.removeEventListener("dashboardPerPageHydrated", applyPerPage);
      window.removeEventListener("userSettingsUpdated", applyPerPage);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const navigate = useNavigate();

  // Fetch customers and users on mount for filter dropdowns and name lookups
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const res = await api.post("/masterdata/customers", {});
        setCustomers(res.data || []);
      } catch {
        setCustomers([]);
      }
    };
    const fetchUsers = async () => {
      try {
        const res = await api.post("/masterdata/users", {});
        const raw = res.data || [];
        const mapped = (Array.isArray(raw) ? raw : []).map((u) => {
          const id = u?.id ?? "";
          const name = u?.name ?? "";
          const email = u?.email ?? "";
          return { id, name, email };
        });
        setUsers(mapped);
        const byId = {};
        mapped.forEach((u) => {
          if (u && u.id !== null && u.id !== "") byId[String(u.id)] = u;
        });
        setUsersById(byId);
      } catch {
        setUsers([]);
        setUsersById({});
      }
    };
    fetchCustomers();
    fetchUsers();
  }, []);

  // The user's open trials with only the fields the task cards/calendar use - see
  // TrialController::dashboardTrials. (The trials table below fetches its own data.)
  function fetchTrials() {
    setLoading(true);
    setError(null);
    const requestId = ++latestFetch.current;
    try {
      api
        .post("/me/dashboard-trials", { scope: effectiveMineOnly ? "mine" : "all", ...JSON.parse(trialQueryKey) })
        .then((res) => {
          if (requestId !== latestFetch.current) return; // a newer request (other filters) is on its way
          let trialsData = [];
          if (res && res.data) {
            trialsData = res.data;
          }
          // apply closed filter like TrialsOverview
          const finalTrials = showClosed
            ? trialsData || []
            : (trialsData || []).filter((t) => {
              return !t.Closed;

            });

          // Calculate and add tasks to trials objects. A trial can have several open tasks
          // at once - any mix of open Visits, Applications and Activities (see
          // getDashboardTasks) - each one gets its own row so it renders as its own
          // dashboard card, rather than a single task overriding/hiding the others.
          const trialsWithTasks = finalTrials.flatMap((trial) => {
            const tasks = getDashboardTasks(trial.diaries, vanoCodes);

            // don't think these are needed? not sure. leaving commented for now
            // trial.highestApplication = getHighestApplicationLetter(trial.diaries);
            // trial.highestAssessment = getHighestAssessmentNumber(trial.diaries);

            if (tasks.length === 0) return [{ ...trial }]; // no pending task, trial as is, taskcard won't render
            return tasks.map((task) => ({
              ...trial,
              task,
              // Distinct key per pending-task row - trial.id itself must stay intact since
              // it's used for navigation/actions (see TaskCardSmall, CancelTaskDialog).
              _rowKey: `${trial.id}-${task.id ?? task.vano}`,
            }));
          });

          setTrials(trialsWithTasks || []);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching /me/trials:", err);
          setError("Failed to load trials.");
          setLoading(false);
        });
    } catch (e) {
      console.error("Unexpected error in fetchTrials:", e);
      setError("Failed to load trials.");
      setLoading(false);
    }
  }

  // On mount and whenever the scope or a trial filter changes; task actions refetch via
  // onTaskComplete/onTaskCancel
  useEffect(() => {
    fetchTrials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveMineOnly, trialQueryKey]);

  // Log settings JSON on dashboard mount
  useEffect(() => {
    try {
      logCurrentSettings();
    } catch { }
  }, []);

  // Listen for settings hydration events to update columns before user interacts
  useEffect(() => {
    function applyHydratedColumns() {
      // Only apply once per page load
      if (hydratedFromSettingsRef.current) return;
      try {
        // Try dashboard-specific storage first, then fallback to overview key
        const raw =
          sessionStorage.getItem(COLUMN_STORAGE_KEY) ||
          sessionStorage.getItem(OVERVIEW_COLUMN_KEY);
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) {
          const filtered = parsed.filter((k) => k !== "id");
          const merged = Array.from(new Set([...filtered, "trialno"]));
          const prevSig = selectedColumns.join("|");
          const newSig = merged.join("|");
          if (prevSig !== newSig) {
            setSelectedColumns(merged);
          }
          // Also hydrate the overviewColumns (left funnel) if not yet applied
          if (!overviewHydratedRef.current) {
            setOverviewColumns(merged);
            overviewHydratedRef.current = true;
          }
          hydratedFromSettingsRef.current = true;
        }
      } catch {
        /* ignore */
      }
    }
    window.addEventListener("dashboardColumnsHydrated", applyHydratedColumns);
    window.addEventListener(
      "trialsOverviewColumnsHydrated",
      applyHydratedColumns
    );
    window.addEventListener("userSettingsUpdated", applyHydratedColumns);
    // Run once in case hydration already happened before listener attached
    applyHydratedColumns();
    return () => {
      window.removeEventListener(
        "dashboardColumnsHydrated",
        applyHydratedColumns
      );
      window.removeEventListener(
        "trialsOverviewColumnsHydrated",
        applyHydratedColumns
      );
      window.removeEventListener("userSettingsUpdated", applyHydratedColumns);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter to only trials where current user is SD, PI, or FT
  const currentUserId = getCurrentUserId();
  const filtered = useMemo(() => {
    // If we don't have a current user id available, skip client-side role filtering
    if (currentUserId == null) return trials;
    return trials.filter((trial) => {
      const userIdStr = String(currentUserId);
      // for some reason sd doesn't fit the convention of the other fields, just go with it
      const sd = trial.studydirector;
      const pi = trial.PI;
      const ft = trial.FT;
      // Accept both string and number comparison
      return [sd, pi, ft].some(
        (role) => role != null && String(role) === userIdStr
      );
    });
    // Fallback: if nothing matched but we do have trials, don't hide everything—return trials unfiltered.
    if (roleMatches.length === 0 && trials.length > 0) {
      if (process.env.NODE_ENV !== "production") {
        console.debug(
          "[Dashboard] Role filtering produced 0 rows; falling back to all trials. currentUserId=",
          userIdStr
        );
      }
      return trials;
    }
    return roleMatches;
  }, [trials, currentUserId]);

  // Show/hide closed trials logic (after user filter)
  const filteredAndOpen = useMemo(() => {
    if (!showClosed) {
      return filtered.filter((trial) => {
        return !trial.Closed;
      });
    }
    return filtered;
  }, [filtered, showClosed]);

  const total = filteredAndOpen.length;
  const totalPages =
    perPage === "all" ? 1 : Math.max(1, Math.ceil(total / perPage));
  const paginated = useMemo(() => {
    if (perPage === "all") return filteredAndOpen;
    const start = (page - 1) * perPage;
    return filteredAndOpen.slice(start, start + perPage);
  }, [filteredAndOpen, page, perPage]);

  // Choose rows to render: if server returned paginated meta use server trials array, otherwise use client-side paginated rows
  const rows = meta ? trials : paginated;

  // Handler for perPage change
  function handlePerPageChange(e) {
    const val = e.target.value;
    if (val === "all") {
      setPerPage("all");
      setPage(1);
      fetchTrials();
    } else {
      const num = Number(val);
      setPerPage(num);
      setPage(1);
      fetchTrials();
    }
  }

  // Handler for page change
  function handlePageChange(newPage) {
    setPage(newPage);
  }

  // Derived helpers
  // Fields treated as simple boolean chips (excluding 'Closed' which now uses status pill styling)
  const BOOLEAN_FIELDS = new Set([
    "Harvest",
    "Destruction",
    "TrialSetupARM",
    "Borders_Sprayed",
    "Weighed_out_Applications",
    "TS",
    "SubcEPA",
    "StOut",
    "ResAr",
    "FinalReport",
    "PegsUp",
    "WOA",
    "DraftReport",
    "GerminationTest",
    "NIT",
    "ReadyForBilling",
    "AdditionalWork",
    "TGW",
    "RecordCleansing",
    "HLLWonly",
    "EndFieldPhase",
    "ARMReady",
    "ReportProofRead",
    "TrialDone",
  ]);
  const DATE_LIKE = new Set(["FieldFaseStart", "FieldFaseEnd"]);
  const LAT_LONG = new Set(["PosGPSLatitude", "PosGPSLongitude"]);
  const NUMBER_HINT = new Set([
    "TreatmentCount",
    "ReplicationCount",
    "ApplicationCount",
    "VisitCount",
    "PlotSizeL",
    "PlotSizeB",
    "Year",
    "NIT_Samples",
    "GT_Samples",
    "TGW_Samples",
    "Contract_Amount",
    "Price_Reduction",
    "Second_Year_Amount",
    "Hours",
  ]);

  function humanLabel(key) {
    const map = {
      customer_id: "Customer",
      projectno: "Project No",
      project_id: "Project ID",
      trialno: "Trial No",
      studydirector: "Study Director",
      pitxt: "PI Text",
      subcontractor: "Subcontractor",
      testitem: "Test Item",
      country_id: "Country",
      crop_id: "Crop",
      locationtarget: "Location",
      study_type_id: "Study Type",
      trial_type_id: "Trial Type",
      host_id: "Host",
      TrialAddress: "Trial Address",
      LocalReference: "Local Reference",
      EarthSample: "Earth Sample",
      TreatmentCount: "Treatments",
      ReplicationCount: "Replications",
      ApplicationCount: "Applications",
      VisitCount: "Visits",
      FieldFaseStart: "Field Phase Start",
      FieldFaseEnd: "Field Phase End",
      CustomerContact: "Customer Contact",
      PlotSizeL: "Plot Size L",
      PlotSizeB: "Plot Size W",
      Year: "Year",
      subscribed_GEP_GL: "Subscribed GEP GL",
      unsubscribed_GEP_GL: "Unsubscribed GEP GL",
      Weighed_out_Applications: "Weighed Out Applications",
      TS: "TS",
      SubcEPA: "Subc EPA",
      Closed: "Status",
      PO: CO_LABEL,
      Noter1: "Notes 1",
      Noter2: "Notes 2",
      Noter3: "Notes 3",
      PosGPSLatitude: "Latitude",
      PosGPSLongitude: "Longitude",
      SubScribed_GEP: "Subscribed GEP",
      UnSubScribed_GEP: "Unsubscribed GEP",
      ReadyForBilling: "Ready For Billing",
      NIT_Samples: "NIT Samples",
      GT_Samples: "GT Samples",
      TGW_Samples: "TGW Samples",
      Contract_Amount: "Contract Amount",
      Price_Reduction: "Price Reduction",
      Second_Year_Amount: "Second Year Amount",
      Hours: "Hours",
      FT: "Field Technician",
      PI: "Principal Investigator",
    };
    // Activity names come from the VA-code catalog (the one source for them)
    return (
      activityLabel(vanoCodes, key, null) ||
      map[key] ||
      key.replace(/_/g, " ").replace(/\b([a-z])/g, (m, c) => c.toUpperCase())
    );
  }

  const columnDefs = useMemo(() => {
    return ALL_TRIAL_FIELDS.map((key) => ({
      key,
      label: humanLabel(key),
      required: key === "trialno",
      headerClass: "",
      cellClass: "",
      render: (t) => {
        const value = t[key];
        // Custom status pill for Closed column (Open / Closed)
        if (key === "Closed") {
          return <TrialStatusBadge closed={t.Closed} />;
        }
        if (BOOLEAN_FIELDS.has(key)) {
          const isTrue = value === 1 || value === true || value === "1";
          return (
            <span
              className={`inline-flex items-center justify-center rounded-full px-2 py-0.5 text-[10px] font-semibold border ${isTrue
                ? "bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700"
                : "bg-gray-100 text-gray-500 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600"
                }`}
            >
              {isTrue ? "Yes" : "No"}
            </span>
          );
        }
        if (DATE_LIKE.has(key) && value) {
          return formatDate(value) || "-";
        }
        if (LAT_LONG.has(key)) {
          // Read whichever way the position is stored (see utils/gps.js)
          const position = parseGps(t.PosGPSLatitude, t.PosGPSLongitude);
          if (!position) return "-";
          return (key === "PosGPSLatitude" ? position.lat : position.lng).toFixed(5);
        }
        // A number of samples only while its activity is Yes (utils/samples.js)
        if (SAMPLE_FIELDS.some((s) => s.field === key)) return shownSamples(t, key) ?? "-";
        if (NUMBER_HINT.has(key)) {
          if (value === null || value === undefined || value === "") return "-";
          return value;
        }
        if (key === "customer_id") {
          return t.customer?.name || "-";
        }
        if (key === "studydirector") {
          return t.study_director?.name || "-";
        }
        if (key === "FT") return t.field_technician?.name || value || "-";
        if (key === "PI") return t.principal_investigator?.name || value || "-";
        // Prevent rendering raw objects
        if (value && typeof value === "object") {
          if (Array.isArray(value)) {
            if (value.length === 0) return "-";
            const joined = value
              .map((v) => (typeof v === "object" ? v?.name || v?.id || "?" : v))
              .join(", ");
            return joined.length > 60 ? joined.slice(0, 57) + "…" : joined;
          }
          if (value.name) return value.name;
          if (value.initials && value.name)
            return `${value.name} (${value.initials})`;
          if (value.initials) return value.initials;
          if (value.id && typeof value.id !== "object") return String(value.id);
          try {
            const json = JSON.stringify(value);
            return json.length > 60 ? json.slice(0, 57) + "…" : json;
          } catch {
            return "[Object]";
          }
        }
        if (value === null || value === undefined || value === "") return "-";
        const str = String(value);
        return str.length > 40 ? str.slice(0, 37) + "…" : str;
      },
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleColumnDefs = columnDefs.filter((c) =>
    selectedColumns.includes(c.key)
  );

  const overviewColumnDefs = useMemo(() => {
    const toName = (val) => {
      if (val == null || val === "") return "";
      const fromUserObj = (u) => u?.name || "";

      if (typeof val === "object") {
        const nm = fromUserObj(val);
        if (nm) return nm;
        const idCandidate = val.id ?? val.userId ?? val.userid;
        if (idCandidate != null) {
          const u =
            usersById?.[String(idCandidate)] ||
            (users || []).find((u) => String(u.id) === String(idCandidate));
          return fromUserObj(u) || String(idCandidate);
        }
        return "";
      }
      const idStr = String(val);
      if (
        idStr === "0" ||
        idStr.toLowerCase() === "null" ||
        idStr.toLowerCase() === "undefined" ||
        idStr.trim() === ""
      )
        return "";
      const u =
        usersById?.[idStr] || (users || []).find((u) => String(u.id) === idStr);
      if (u) return fromUserObj(u);
      return idStr;
    };
    const short = (s) =>
      typeof s === "string" && s.length > 20 ? s.slice(0, 20) + "…" : s || "-";

    return [
      {
        key: "protocolno",
        label: "Protocol No",
        render: (t) => t.protocolno || "-",
      },
      { key: "trialno", label: "Trial No", render: (t) => t.trialno || "-" },
      {
        key: "customer",
        label: "Customer",
        render: (t) => t.customer?.name || "-",
      },
      {
        key: "studydirector",
        label: "Study Director",
        render: (t) => {
          const n = toName(t.studydirector);
          return short(n);
        },
      },
      {
        key: "PI",
        label: "Principal Investigator",
        render: (t) => {
          const possible = t.principal_investigator ?? null;
          const n = toName(possible);
          return short(n);
        },
      },
      {
        key: "FT",
        label: "Field Technician",
        render: (t) => {
          const possible = t.FT ?? t.field_technician ?? null;
          const n = toName(possible);
          return short(n);
        },
      },
      {
        key: "status",
        label: "Status",
        render: (t) => <TrialStatusBadge closed={t.Closed} />,
      },
    ];
  }, [users, usersById]);
  const visibleOverviewCols = overviewColumnDefs.filter((c) =>
    overviewColumns.includes(c.key)
  );
  function toggleOverviewColumn(key) {
    setOverviewColumns((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  }
  async function saveOverviewColumns() {
    setSavingOverviewCols(true);
    const cols = [...overviewColumns];
    // Persist to dashboard-specific column storage so this page can keep its own view
    sessionStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(cols));
    const ok = await updateUserSettings({ dashboard_trial_columns: cols });
    if (!ok) console.warn("Failed to persist dashboard overview column config");
    setSavingOverviewCols(false);
    setShowOverviewColumnConfig(false);
    setOverviewColumns(cols);
  }

  async function saveColumnConfig() {
    setSavingColumns(true);
    const cols = Array.from(new Set([...selectedColumns, "trialno"])); // ensure required
    sessionStorage.setItem(COLUMN_STORAGE_KEY, JSON.stringify(cols));
    const ok = await updateUserSettings({ dashboard_trial_columns: cols });
    if (!ok) {
      console.warn("Failed to persist column config to server, kept locally.");
    }
    setSavingColumns(false);
    setShowColumnConfig(false);
    setSelectedColumns(cols);
  }

  function toggleColumn(key) {
    if (key === "trialno") return; // required
    setSelectedColumns((prev) => {
      const has = prev.includes(key);
      if (has) return prev.filter((k) => k !== key);
      return [...prev, key];
    });
  }

  // Close column config on outside click or ESC
  useEffect(() => {
    if (!showColumnConfig) return;
    function handleClickAway(e) {
      if (!columnMenuRef.current) return;
      if (!columnMenuRef.current.contains(e.target)) {
        setShowColumnConfig(false);
      }
    }
    function handleKey(e) {
      if (e.key === "Escape") setShowColumnConfig(false);
    }
    document.addEventListener("mousedown", handleClickAway);
    document.addEventListener("touchstart", handleClickAway);
    window.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleClickAway);
      document.removeEventListener("touchstart", handleClickAway);
      window.removeEventListener("keydown", handleKey);
    };
  }, [showColumnConfig]);

  // The open tasks that pass the task filters (status, type, assigned to, old overdue) - for the
  // cards and the calendar marks
  const visibleTaskRows = useMemo(
    () => trials.filter((t) => !t.Closed && t.task && taskPasses(t.task, { ...dashFilters, mineOnly: effectiveMineOnly }, new Date(), t, currentUserId)),
    [trials, dashFilters, effectiveMineOnly, currentUserId]
  );
  // The cards: further narrowed by the calendar's day or week
  const shownTaskRows = useMemo(() => {
    if (!dateSelection) return visibleTaskRows;
    return visibleTaskRows.filter((t) => {
      const due = t.task.dueDate ? new Date(t.task.dueDate) : null;
      return due && due >= dateSelection.from && due <= dateSelection.to;
    });
  }, [visibleTaskRows, dateSelection]);
  const overdueCount = shownTaskRows.filter((t) => getTaskBadge(t.task).status === "overdue").length;
  // With a task filter set, the table shows just the trials with a matching task
  const tableIds = useMemo(
    () => (hasTaskFilters(dashFilters) ? [...new Set(visibleTaskRows.map((t) => t.id))] : null),
    [dashFilters, visibleTaskRows]
  );
  // The table's order: the trial with the most urgent matching task first (the earliest due date,
  // so the most overdue first; tasks without a date last) - customer feedback 3
  const priorityIds = useMemo(() => {
    const due = (t) => (t.task.dueDate ? new Date(t.task.dueDate).getTime() : Infinity);
    return [...new Set([...visibleTaskRows].sort((a, b) => due(a) - due(b)).map((t) => t.id))];
  }, [visibleTaskRows]);
  const selectionTitle = !dateSelection
    ? "All open tasks"
    : dateSelection.kind === "day"
      ? `Tasks on ${formatDate(dateSelection.from)}`
      : `Tasks in week ${isoWeek(dateSelection.from)}`;
  const countText = `${shownTaskRows.length} open ${shownTaskRows.length === 1 ? "task" : "tasks"} · ${overdueCount} overdue`;

  return (
    <>
      <div className="mb-2 flex items-center gap-2">
        <h1 className="text-2xl font-bold">{effectiveMineOnly ? "My Trials" : "All Trials"}</h1>
      </div>
      {/* One filter panel for the task cards, the calendar and the trials table */}
      <TrialSearchFilters
        compact
        filters={dashFilters}
        onChange={updateDashFilters}
        onReset={resetDashFilters}
        resetDisabled={!differsFromDefaults(dashFilters)}
        extra={(
          <DashboardTaskFilters
            filters={dashFilters}
            onChange={updateDashFilters}
            users={users}
            mineOnlyOverriddenBy={assigneeIsOther ? (users.find((u) => String(u.id) === String(dashFilters.assignee))?.name || "another user") : null}
          />
        )}
      />
      {error && (
        <div role="alert" className="mb-3 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          <span>{error} Your tasks and calendar could not be shown.</span>
          <button
            type="button"
            onClick={fetchTrials}
            disabled={loading}
            className="shrink-0 rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:bg-transparent dark:text-red-200 dark:hover:bg-red-900/40"
          >
            {loading ? "Retrying..." : "Retry"}
          </button>
        </div>
      )}

      <ResizablePanelGroup direction="vertical" className="min-h-screen">
        <ResizablePanel defaultSize={35} className="flex flex-col">
          {/* Task cards go here :) */}
          <div className="flex-1 flex flex-col lg:flex-row gap-6 p-4 min-h-0">
            <div className="hidden sm:block flex-shrink-0">
              <TaskCalendar
                trials={visibleTaskRows}
                selection={dateSelection}
                onSelectionChange={setDateSelection}
              />
            </div>
            <div className="flex-1 min-h-0 flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm">
                  <span className="font-semibold" data-testid="task-filter-title">{selectionTitle}</span>
                  <span className="mx-2 text-gray-400">·</span>
                  <span data-testid="task-count">{countText}</span>
                </p>
                <TaskStatusLegend />
              </div>
              <TaskList
                trials={shownTaskRows}
                onTaskComplete={fetchTrials}
                currentUserId={currentUserId}
                showAssignee={!effectiveMineOnly}
                emptyText={dateSelection ? `No open tasks ${dateSelection.kind === "day" ? `on ${formatDate(dateSelection.from)}` : `in week ${isoWeek(dateSelection.from)}`}` : "No open tasks"}
              />
            </div>
          </div>
        </ResizablePanel>

        <div className="flex justify-center">
          <ResizableHandle
            withHandle
            className="border hover:border-4 duration-200 rounded m-10"
          />
        </div>

        <ResizablePanel defaultSize={65} className="flex flex-col min-h-0 p-4">
          {/* What the table lists: the task filters pick the trials, but SD, PI and FT are the trial's */}
          <p className="mb-2 text-sm text-gray-600 dark:text-gray-300" data-testid="dashboard-table-note">
            {hasTaskFilters(dashFilters)
              ? "Trials with at least one open task matching the filters above, the most urgent task first. Study Director, PI and FT are the trial's people, not who the tasks are assigned to."
              : "Trials matching the filters above, the one with the most urgent open task first."}
          </p>
          <TrialsTable
            // The same trials as the cards: the panel's trial filters, "Mine only", and - with a task
            // filter set - only the trials with a matching task
            externalFilters={JSON.parse(trialQueryKey)}
            mineOnly={effectiveMineOnly}
            onlyIds={tableIds}
            priorityIds={priorityIds}

            // Storage keys for dashboard-specific persistence
            columnStorageKey="dashboardTrialColumns"
            perPageStorageKey="dashboardTrialsPerPage"
            storagePrefix="dashboard_trials"

            // Default columns for dashboard
            defaultColumns={["trialno", "customer", "studydirector", "PI", "FT", "status"]}

            // The filters are the dashboard's panel above (not the table's own)
            showSearchFilters={false}

            // Enable all features
            showFilters={true}
            showProtocolFilter={false}
            showCustomerFilter={false}
            showStudyDirectorFilter={false}
            // Only open trials here (the trials overview has "Show closed trials")
            showClosedToggle={false}
            showColumnConfig={true}
            showActions={true}

            // Mass update integration
            massUpdateMode={massUpdateMode}
            selectedTrialIds={selectedTrialIds}
            onSelectionChange={setSelectedTrialIds}

            // Navigation callback
            onTrialSelect={(trial) => {
              navigate(`/trials/${trial.id}`, { state: { from: "/" } }); // Back goes to the dashboard
            }}

            // Current user for actions
            currentUserId={currentUserId}

            // Custom styling for proper height in resizable panel
            className="flex-1 min-h-0"
            tableClassName=""
          />
        </ResizablePanel>
      </ResizablePanelGroup>
    </>
  );
}
